export const WEFAT_NAVIGATION_API_NAMES = Object.freeze([
    'navigateTo',
    'redirectTo',
    'navigateBack',
    'reLaunch',
    'switchTab',
]);
export class WxNavigationRuntimeError extends Error {
    code;
    errCode;
    errMsg;
    api;
    remediation;
    constructor(code, api, reason, remediation, cause) {
        const errMsg = `${api}:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxNavigationRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.api = api;
        this.remediation = remediation;
        if (cause !== undefined)
            Object.defineProperty(this, 'cause', { value: cause });
    }
}
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const URL_KEYS = new Set([...CALLBACK_KEYS, 'url']);
const BACK_KEYS = new Set([...CALLBACK_KEYS, 'delta']);
function optionError(api, reason) {
    return new WxNavigationRuntimeError('WF7006', api, reason, 'Pass a registered Mini Program route URL and documented callable callbacks.');
}
function driverError(api, error) {
    if (error instanceof WxNavigationRuntimeError)
        return error;
    const code = typeof error === 'object' && error !== null && Reflect.get(error, 'code') === 'WF7005'
        ? 'WF7005'
        : 'WF7006';
    const reason = error instanceof Error ? error.message : String(error);
    return new WxNavigationRuntimeError(code, api, reason, code === 'WF7005'
        ? 'Navigate to a route registered in app.json and preserve its normalized query string.'
        : 'Use navigation only after application bootstrap and keep the requested page-stack transition valid.', error);
}
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function readOptions(api, raw, allowed, optional) {
    if (raw === undefined && optional)
        return {};
    if (!isPlainRecord(raw))
        throw optionError(api, 'options must be a plain object');
    for (const key of Reflect.ownKeys(raw)) {
        const descriptor = typeof key === 'string' ? Object.getOwnPropertyDescriptor(raw, key) : undefined;
        if (typeof key !== 'string' || !allowed.has(key)) {
            throw optionError(api, `unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(api, `option field ${key} must be a direct data property`);
        }
        if (CALLBACK_KEYS.has(key) && descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(api, `${key} must be a function when provided`);
        }
    }
    return raw;
}
function callbacks(options) {
    return {
        ...(typeof options.success === 'function' ? { success: options.success } : {}),
        ...(typeof options.fail === 'function' ? { fail: options.fail } : {}),
        ...(typeof options.complete === 'function'
            ? { complete: options.complete }
            : {}),
    };
}
function invokeCallback(callback, value, api, kind, report) {
    if (callback === undefined)
        return;
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, api, kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => { throw reportError; });
                return;
            }
        }
        queueMicrotask(() => { throw error; });
    }
}
function run(api, callbackSet, operation, report) {
    const task = Promise.resolve().then(operation).then(() => Object.freeze({ errMsg: `${api}:ok` }));
    if (callbackSet.success === undefined && callbackSet.fail === undefined && callbackSet.complete === undefined) {
        return task.catch((error) => { throw driverError(api, error); });
    }
    void task.then((result) => {
        invokeCallback(callbackSet.success, result, api, 'success', report);
        invokeCallback(callbackSet.complete, result, api, 'complete', report);
    }, (error) => {
        const failure = driverError(api, error);
        invokeCallback(callbackSet.fail, failure, api, 'fail', report);
        invokeCallback(callbackSet.complete, failure, api, 'complete', report);
    });
    return undefined;
}
export function createNavigationWxApis(options) {
    if (!isPlainRecord(options) || !isPlainRecord(options.driver)) {
        throw new TypeError('createNavigationWxApis requires a plain driver object');
    }
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    for (const api of WEFAT_NAVIGATION_API_NAMES) {
        if (typeof options.driver[api] !== 'function') {
            throw new TypeError(`navigation driver requires ${api}`);
        }
    }
    function urlOperation(api, raw) {
        const parsed = readOptions(api, raw, URL_KEYS, false);
        if (typeof parsed.url !== 'string' || parsed.url.length === 0) {
            throw optionError(api, 'url must be a non-empty string');
        }
        const callbackSet = callbacks(parsed);
        return run(api, callbackSet, () => options.driver[api](parsed.url), options.onCallbackError);
    }
    return Object.freeze({
        navigateTo: (raw) => urlOperation('navigateTo', raw),
        redirectTo: (raw) => urlOperation('redirectTo', raw),
        reLaunch: (raw) => urlOperation('reLaunch', raw),
        switchTab: (raw) => urlOperation('switchTab', raw),
        navigateBack(raw) {
            const parsed = readOptions('navigateBack', raw, BACK_KEYS, true);
            const delta = parsed.delta ?? 1;
            if (typeof delta !== 'number' || !Number.isInteger(delta) || delta < 1) {
                throw optionError('navigateBack', 'delta must be a positive integer');
            }
            return run('navigateBack', callbacks(parsed), () => options.driver.navigateBack(delta), options.onCallbackError);
        },
    });
}
//# sourceMappingURL=navigation-adapter.js.map